// @spec SHL-STR-050
// Color utilities for organization branding contrast validation and color manipulation

/**
 * Parse a hex color string to RGB values.
 * Supports both 3-character (#RGB) and 6-character (#RRGGBB) formats.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Handle 3-character hex
  let r: number, g: number, b: number;
  if (cleanHex.length === 3) {
    const char0 = cleanHex[0];
    const char1 = cleanHex[1];
    const char2 = cleanHex[2];
    if (char0 === undefined || char1 === undefined || char2 === undefined) {
      return null;
    }
    r = parseInt(char0 + char0, 16);
    g = parseInt(char1 + char1, 16);
    b = parseInt(char2 + char2, 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.slice(0, 2), 16);
    g = parseInt(cleanHex.slice(2, 4), 16);
    b = parseInt(cleanHex.slice(4, 6), 16);
  } else {
    return null;
  }

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return null;
  }

  return { r, g, b };
}

/**
 * Calculate the relative luminance of an RGB color.
 * Based on WCAG 2.1 formula.
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  };

  const rs = toLinear(r);
  const gs = toLinear(g);
  const bs = toLinear(b);

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Validate WCAG AA contrast ratio between two colors.
 *
 * WCAG AA requires:
 * - 4.5:1 for normal text
 * - 3:1 for large text (18pt+ or 14pt bold)
 *
 * @param foreground - Foreground color in hex format (e.g., "#2563EB")
 * @param background - Background color in hex format (e.g., "#FFFFFF")
 * @returns Object with isValid flag and calculated contrast ratio
 */
export function validateColorContrast(
  foreground: string,
  background: string
): { isValid: boolean; ratio: number } {
  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);

  if (!fgRgb || !bgRgb) {
    return { isValid: false, ratio: 0 };
  }

  const fgLuminance = getRelativeLuminance(fgRgb.r, fgRgb.g, fgRgb.b);
  const bgLuminance = getRelativeLuminance(bgRgb.r, bgRgb.g, bgRgb.b);

  // Calculate contrast ratio per WCAG formula
  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);
  const ratio = (lighter + 0.05) / (darker + 0.05);

  // Round to 2 decimal places for comparison
  const roundedRatio = Math.round(ratio * 100) / 100;

  // WCAG AA requires 4.5:1 for normal text
  return {
    isValid: roundedRatio >= 4.5,
    ratio: roundedRatio,
  };
}

/**
 * Clamp a number between min and max values.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Convert RGB to HSL.
 */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to RGB.
 */
function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Convert RGB to hex color string.
 */
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (c: number) => {
    const hex = clamp(c, 0, 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Color shade mapping for Tailwind-style color scales.
 * Defines the lightness adjustments for each shade level.
 */
const SHADE_LIGHTNESS: Record<string, number> = {
  '50': 97,
  '100': 94,
  '200': 86,
  '300': 77,
  '400': 66,
  '500': 55,
  '600': 44,
  '700': 37,
  '800': 30,
  '900': 22,
  '950': 13,
};

/**
 * Generate color variants from a base color.
 *
 * Creates a Tailwind-style color scale (50-950) based on the input color.
 * The input color will be used as the 500-600 range base.
 *
 * @param baseColor - Base color in hex format (e.g., "#2563EB")
 * @returns Object with shade keys (50, 100, 200, ..., 950) and hex values
 */
export function generateColorVariants(baseColor: string): Record<string, string> {
  const rgb = hexToRgb(baseColor);
  if (!rgb) {
    // Return a default blue scale if invalid color
    return {
      '50': '#eff6ff',
      '100': '#dbeafe',
      '200': '#bfdbfe',
      '300': '#93c5fd',
      '400': '#60a5fa',
      '500': '#3b82f6',
      '600': '#2563eb',
      '700': '#1d4ed8',
      '800': '#1e40af',
      '900': '#1e3a8a',
      '950': '#172554',
    };
  }

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const variants: Record<string, string> = {};

  for (const [shade, lightness] of Object.entries(SHADE_LIGHTNESS)) {
    // Adjust saturation slightly based on lightness
    // Lighter shades tend to be slightly less saturated
    const saturationFactor = lightness > 80 ? 0.8 : lightness < 30 ? 0.9 : 1;
    const adjustedSaturation = clamp(hsl.s * saturationFactor, 0, 100);

    const newRgb = hslToRgb(hsl.h, adjustedSaturation, lightness);
    variants[shade] = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  }

  return variants;
}

/**
 * Check if a string is a valid hex color.
 *
 * @param color - Color string to validate
 * @returns True if valid hex color format
 */
export function isValidHexColor(color: string): boolean {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color);
}
